import React from 'react';
import { Tooltip, CircularProgress } from '@mui/material';
import { PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import styles from '../../../styles/chips/chipComponent.module.scss';

const ChipComponent = ({ 
  children,
  variant = "default", 
  size = "small",
  className = "",
  sx = {}, 
  showTooltip = true,
  hover = false,
  icon = null,
  editable = false,
  editIcon = null,
  onEdit = null,
  removable = false,
  onDelete = null,
  clickable = false,
  onClick = null,
  disabled = false,
  loading = false,
  ...props 
}) => {
  const baseStyles = {
    // Only include non-color styles here, let CSS classes handle colors
    ...sx,
  };

  // Combine CSS classes
  const chipClasses = [
    styles['chip-base'],
    styles[`chip-${variant}`],
    styles[`chip-${size}`],
    hover ? styles['chip-hover'] : null,
    editable ? styles['chip-editable'] : null,
    clickable && !disabled ? styles['chip-clickable'] : null,
    disabled ? styles['chip-disabled'] : null,
    className
  ].filter(Boolean).join(' ');

  // Get text content for tooltip
  const textContent = typeof children === 'string' ? children : '';
  
  // Default edit icon is PencilIcon
  const EditIcon = editIcon || <PencilIcon />;
  
  const handleChipClick = (e) => {
    if (disabled) return;
    
    if (editable && onEdit) {
      e.stopPropagation();
      onEdit(e);
    } else if (clickable && onClick) {
      e.stopPropagation();
      onClick(e);
    }
  };
  
  const handleRemoveClick = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(e);
    }
  };
  
  const chipElement = (
    <div 
      className={chipClasses}
      style={baseStyles}
      onClick={(editable || clickable) && !disabled ? handleChipClick : undefined}
      {...props}
    >
      {loading ? (
        <span className={styles['chip-icon']}>
          <CircularProgress size={14} thickness={3} sx={{ display: 'block' }} />
        </span>
      ) : icon && (
        <span className={styles['chip-icon']}>{icon}</span>
      )}
      <p>{children}</p>
      {!loading && editable && onEdit && (
        <span className={styles['chip-edit-icon']}>
          {EditIcon}
        </span>
      )}
      {!loading && removable && onDelete && (
        <span 
          className={styles['chip-remove-icon']}
          onClick={handleRemoveClick}
        >
          <XMarkIcon />
        </span>
      )}
    </div>
  );

  // Wrap with MUI Tooltip if showTooltip is true
  if (showTooltip && textContent) {
    return (
      <Tooltip title={textContent} >
        {chipElement}
      </Tooltip>
    );
  }

  return chipElement;
};

export default ChipComponent;
